import { useQuery } from '@tanstack/react-query';

/**
 * Interface for the parsed documentation data
 */
export interface DocumentData {
  /** The raw markdown content */
  content: string;
  /** The document title extracted from the first heading or filename */
  title: string;
  /** The file path */
  path: string;
}

/**
 * Interface for the hook return value
 */
interface UseDocumentationReturn {
  /** The parsed document data */
  document: DocumentData | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether the document was not found */
  isNotFound: boolean;
  /** Function to refetch the document */
  refetch: () => void;
}

/**
 * Extracts the title from markdown content
 * Looks for the first H1 heading, otherwise uses the filename
 *
 * @param content - The markdown content
 * @param path - The file path
 * @returns The extracted title
 */
function extractTitle(content: string, path: string): string {
  // Try to find first H1 heading
  const h1Match = content.match(/^#\s+(.+)$/m);
  if (h1Match) {
    return h1Match[1].trim();
  }

  // Fallback to filename without extension
  const filename = path.split('/').pop() || '';
  return filename.replace(/\.md$/, '').replace(/-/g, ' ');
}

/**
 * TELV1-604 — thrown when a doc path has no markdown file behind it. Checked
 * by `name` rather than `instanceof` so it survives any transpile target.
 */
export class DocNotFoundError extends Error {
  constructor(path: string) {
    super(`Document not found: ${path}`);
    this.name = 'DocNotFoundError';
  }
}

export const isDocNotFoundError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'DocNotFoundError';

/**
 * TELV1-604 — the host's SPA fallback answers a missing `/docs/*.md` with the
 * app's own index.html and a 200, which the viewer would otherwise render as
 * markdown. Markdown arrives as text/markdown, text/plain or
 * application/octet-stream depending on the server, so only an explicit HTML
 * content-type or an HTML document body counts; a missing content-type does not.
 */
export const isHtmlDocumentResponse = (contentType: string | null, body: string): boolean => {
  if (contentType?.toLowerCase().includes('text/html')) return true;
  const start = body.trimStart().slice(0, 16).toLowerCase();
  return start.startsWith('<!doctype') || start.startsWith('<html');
};

/**
 * Custom hook for fetching and parsing markdown documentation files
 *
 * @description
 * This hook fetches markdown files from the `/docs/` directory and parses them into
 * a structured format. It handles loading states, errors, and 404 responses.
 *
 * Features:
 * - Automatic title extraction from markdown content or filename
 * - Built-in caching via React Query
 * - Error handling with 404 detection
 * - TypeScript type safety
 *
 * @param path - The relative path to the markdown file (e.g., "getting-started/introduction.md")
 *
 * @returns An object containing the document data, loading state, and error state
 *
 * @example
 * ```tsx
 * function DocumentationPage() {
 *   const { document, isLoading, error, isNotFound } = useDocumentation('getting-started/introduction.md');
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (isNotFound) return <div>Document not found</div>;
 *   if (error) return <div>Error loading document</div>;
 *
 *   return <MarkdownRenderer content={document.content} />;
 * }
 * ```
 */
export function useDocumentation(path: string | undefined | null): UseDocumentationReturn {
  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = useQuery<DocumentData, Error>({
    queryKey: ['documentation', path],
    queryFn: async () => {
      if (!path) {
        throw new Error('No path provided');
      }

      // Fetch the markdown file from the public docs directory
      // Using native fetch instead of axios to avoid interceptors
      const response = await fetch(`/docs/${path}`);

      if (response.status === 404) {
        throw new DocNotFoundError(path);
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
      }

      const content = await response.text();
      if (isHtmlDocumentResponse(response.headers.get('content-type'), content)) {
        throw new DocNotFoundError(path);
      }

      const title = extractTitle(content, path);

      return {
        content,
        title,
        path,
      };
    },
    enabled: !!path,
    retry: false, // Don't retry on 404
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const isNotFound = isDocNotFoundError(error);

  return {
    document,
    isLoading,
    error,
    isNotFound,
    refetch,
  };
}
